/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { GroupLevelItem } from '#PRIVATE_Axis/groupAxis/hierarchicalUtils';
import { ChartGroupInfo } from '#utils/UNSAFE_visTypes/chart';

type isGroupNavigableType = (levelIndex: number, groupIndex: number) => boolean;

/**
 * Returns the parent group for the navigable item.
 * @param levelsArray The levels array.
 * @param levelIndex The levelIndex of current naviable group..
 * @param groupIndex The groupIndex of current navigable group.
 * @returns
 */
export function getParentGroup(
  levelsArray: GroupLevelItem[][],
  levelIndex: number,
  groupIndex: number,
  isGroupNavigable: isGroupNavigableType
) {
  const path = levelsArray[levelIndex][groupIndex].path;
  if (path.length === 1) {
    return {
      levelIndex,
      groupIndex
    };
  }

  let lIndex = Math.max(0, levelIndex - 1);
  const groupItem = levelsArray[levelIndex][groupIndex];
  const { start, end } = groupItem;

  while (lIndex >= 0) {
    for (let i = 0; i < levelsArray[lIndex].length; i++) {
      const { start: s, end: e } = levelsArray[lIndex][i];
      if (s <= start && end <= e) {
        if (isGroupNavigable(lIndex, i)) {
          return { groupIndex: i, levelIndex: lIndex };
        }
      }
    }
    lIndex -= 1;
  }

  return {
    levelIndex,
    groupIndex
  };
}

/**
 * @param levelsArray The levels array.
 * @param levelIndex The levelIndex of current navigable item.
 * @param groupIndex The groupIndex of current navigable item.
 * @returns
 */
export function getChildGroup(
  levelsArray: GroupLevelItem[][],
  levelIndex: number,
  groupIndex: number,
  isGroupNavigable: isGroupNavigableType
) {
  if (levelIndex === levelsArray.length - 1) {
    return { levelIndex, groupIndex };
  }

  const lIndex = Math.min(levelsArray.length - 1, levelIndex + 1);

  const groupItem = levelsArray[levelIndex][groupIndex];
  const { start, end } = groupItem;

  while (lIndex <= levelsArray.length - 1) {
    for (let i = 0; i < levelsArray[lIndex].length; i++) {
      const { start: s, end: e } = levelsArray[lIndex][i];
      if (start <= s && e <= end) {
        if (isGroupNavigable(lIndex, i)) {
          return { groupIndex: i, levelIndex: lIndex };
        }
      }
    }
  }
  return {
    levelIndex,
    groupIndex
  };
}

export function getNextGroup(
  levelsArray: GroupLevelItem[][],
  info: ChartGroupInfo,
  isGroupNavigable: isGroupNavigableType
): ChartGroupInfo | undefined {
  const groupIndex = info.groupIndex! + 1;
  const levelIndex = info.levelIndex!;

  const maxgroup = levelsArray[levelIndex].length - 1;

  if (groupIndex > maxgroup) {
    return;
  }

  if (isGroupNavigable(levelIndex!, groupIndex)) {
    return { groupIndex, levelIndex };
  }
  return getNextGroup(levelsArray, { groupIndex, levelIndex }, isGroupNavigable);
}

export function getPreviousGroup(
  info: ChartGroupInfo,
  isGroupNavigable: isGroupNavigableType
): ChartGroupInfo | undefined {
  const groupIndex = info.groupIndex! - 1;
  const levelIndex = info.levelIndex;
  if (groupIndex < 0) {
    return;
  }
  if (isGroupNavigable(levelIndex!, groupIndex)) {
    return { groupIndex, levelIndex };
  }
  return getPreviousGroup({ groupIndex, levelIndex }, isGroupNavigable);
}

export function getGroupNavUtil(
  levelsArray: GroupLevelItem[][],
  drilling: 'on' | 'off' | 'groupsOnly',
  isRtl: boolean,
  isHoriz: boolean,
  isGroupLabelRendered?: (levelIndex: number, groupIndex: number) => boolean
) {
  const isGroupNavigable = (levelIndex: number, groupIndex: number) => {
    const group = levelsArray[levelIndex][groupIndex].item;
    const groupName = group.name || group.id;
    const groupsDrilling = group.drilling;
    const isLabelRendered = !!isGroupLabelRendered?.(levelIndex, groupIndex);
    return (
      isLabelRendered &&
      groupName != null &&
      (drilling === 'on' || drilling === 'groupsOnly') &&
      groupsDrilling != 'off'
    );
  };

  const getDefaultNavGroup = () => {
    for (let levelIndex = 0; levelIndex < levelsArray.length; levelIndex++) {
      const numGroups = levelsArray[levelIndex].length;
      for (let groupIndex = 0; groupIndex < numGroups; groupIndex++) {
        if (isGroupNavigable(levelIndex, groupIndex)) {
          return { groupIndex, levelIndex };
        }
      }
    }
    return { groupIndex: 0, levelIndex: 0 };
  };

  const getNextNavGroup = (
    info: ChartGroupInfo,
    arrow: 'ArrowRight' | 'ArrowLeft' | 'ArrowUp' | 'ArrowDown'
  ) => {
    let nextGroup;

    if (arrow === 'ArrowRight') {
      if (isHoriz) {
        nextGroup = !isRtl
          ? getChildGroup(levelsArray, info.levelIndex!, info.groupIndex!, isGroupNavigable)
          : getParentGroup(levelsArray, info.levelIndex!, info.groupIndex!, isGroupNavigable);
      } else {
        nextGroup = isRtl
          ? getPreviousGroup(info, isGroupNavigable)
          : getNextGroup(levelsArray, info, isGroupNavigable);
      }
    } else if (arrow === 'ArrowLeft') {
      if (isHoriz) {
        nextGroup = isRtl
          ? getChildGroup(levelsArray, info.levelIndex!, info.groupIndex!, isGroupNavigable)
          : getParentGroup(levelsArray, info.levelIndex!, info.groupIndex!, isGroupNavigable);
      } else {
        nextGroup = isRtl
          ? getNextGroup(levelsArray, info, isGroupNavigable)
          : getPreviousGroup(info, isGroupNavigable);
      }
    } else if (arrow === 'ArrowDown') {
      nextGroup = isHoriz
        ? getNextGroup(levelsArray, info, isGroupNavigable)
        : getParentGroup(levelsArray, info.levelIndex!, info.groupIndex!, isGroupNavigable);
    } else if (arrow === 'ArrowUp') {
      nextGroup = isHoriz
        ? getPreviousGroup(info, isGroupNavigable)
        : getChildGroup(levelsArray, info.levelIndex!, info.groupIndex!, isGroupNavigable);
    }
    return nextGroup || info;
  };

  return { getNextNavGroup, getDefaultNavGroup };
}
